import {
  SummaryCell,
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { EffortContactItem } from 'library/types';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { CreateEffortContactDialog, DeleteEffortContactDialog, UpdateEffortContactDialog } from './components';

const EffortContacts = () => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [effortContacts, setEffortContacts] = useState<EffortContactItem[]>([]);
  const [openDeleteEffortContact, setOpenDeleteEffortContact] = useState(false);
  const [openCreateEffortContact, setOpenCreateEffortContact] = useState(false);
  const [openUpdateEffortContact, setOpenUpdateEffortContact] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'gibTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
      {
        field: 'emails',
        headerName: intl.formatMessage({ id: 'effortContacts.emails', defaultMessage: 'E-Postalar' }),
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: SummaryCell({ mode: 'emails' }),
        valueGetter: (_value, row) => (row.emails ?? []).join('; '),
      },
    ],
    [intl]
  );

  // sync helper functions
  const handleCloseDeleteEffortContact = () => setOpenDeleteEffortContact(false);
  const handleClickOpenDeleteEffortContact = () => {
    const selectedId = requireSingleRowSelection(rowSelectionModel);
    if (selectedId === null) return;

    // non-deletable item control
    if (effortContacts.find((effortContact) => effortContact.id === selectedId)?.emails === null) {
      enqueueSnackbar(<FormattedMessage id="checkbox.canNotDelete" defaultMessage="Bu satır silinemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteEffortContact(true);
  };

  const handleCloseCreateEffortContact = () => setOpenCreateEffortContact(false);
  const handleClickOpenCreateEffortContact = () => setOpenCreateEffortContact(true);

  const handleCloseUpdateEffortContact = () => setOpenUpdateEffortContact(false);
  const handleClickOpenUpdateEffortContact = () => {
    const selectedId = requireSingleRowSelection(rowSelectionModel);
    if (selectedId === null) return;

    // non-editable item control
    if (effortContacts.find((effortContact) => effortContact.id === selectedId)?.emails === null) {
      enqueueSnackbar(<FormattedMessage id="checkbox.canNotUpdate" defaultMessage="Bu satır düzenlenemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateEffortContact(true);
  };

  // async helper functions
  const getEffortContacts = async () => {
    try {
      setLoading(true);

      const response = await apiService.getEffortContacts();

      if (response.isSuccess) {
        setEffortContacts(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    getEffortContacts();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-effort-contact']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateEffortContact}>
              <FormattedMessage id="effortContacts.createEffortContact" defaultMessage="Efor Muhatabı Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-effort-contact']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateEffortContact}>
              <FormattedMessage id="effortContacts.updateEffortContact" defaultMessage="Efor Muhatabı Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-effort-contact']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteEffortContact}>
              <FormattedMessage id="effortContacts.deleteEffortContact" defaultMessage="Efor Muhatabı Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateEffortContactDialog
        open={openCreateEffortContact}
        handleClose={handleCloseCreateEffortContact}
        refresh={getEffortContacts}
      />
      <UpdateEffortContactDialog
        open={openUpdateEffortContact}
        effortContactId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateEffortContact}
        refresh={getEffortContacts}
      />
      <DeleteEffortContactDialog
        open={openDeleteEffortContact}
        effortContactId={rowSelectionModel[0]}
        handleClose={handleCloseDeleteEffortContact}
        refresh={getEffortContacts}
        setRowSelectionModel={setRowSelectionModel}
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={effortContacts}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EffortContacts;
